import { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { selectUser } from "../../store/slices/auth/slice";
import {
  ProgressManager,
  isLocalProgressAhead,
} from "../../api/services/lessonProgress";

const SAVE_ERROR_TOAST_INTERVAL = 60000;

const VimeoPlayer = ({
  vimeoVideoId,
  vimeoHash,
  lessonId,
  initialProgress,
  onCompletion,
  autoPlay = false,
}) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const progressManagerRef = useRef(null);
  const hasCompletedRef = useRef(false);
  const saveErrorCountRef = useRef(0);
  const lastSaveErrorToastRef = useRef(0);
  const [hasError, setHasError] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const userId = user?.id || null;

  // Initialize Vimeo player and ProgressManager
  useEffect(() => {
    if (!containerRef.current || !vimeoVideoId) return undefined;

    // Reset refs for new lesson
    hasCompletedRef.current = initialProgress?.isCompleted || false;
    saveErrorCountRef.current = 0;

    // --- 1. Kreiraj ProgressManager ---
    const progressManager = new ProgressManager(
      lessonId,
      dispatch,
      (result) => {
        saveErrorCountRef.current = 0;
        if (result.courseCompleted || result.progress?.isCompleted) {
          if (!hasCompletedRef.current) {
            hasCompletedRef.current = true;
            if (onCompletion) {
              // Periodični save / pauza — samo osvežavanje statusa, bez navigacije
              onCompletion(result, "progress");
            }
          }
        }
      },
      (error) => {
        console.error("Progress save error:", error);
        saveErrorCountRef.current += 1;
        const now = Date.now();
        if (
          saveErrorCountRef.current >= 2 &&
          now - lastSaveErrorToastRef.current > SAVE_ERROR_TOAST_INTERVAL
        ) {
          lastSaveErrorToastRef.current = now;
          toast.error("Napredak se trenutno ne čuva — proverite konekciju.");
        }
      },
      userId,
    );
    progressManagerRef.current = progressManager;

    // Pročitaj lokalni snimak JEDNOM (pre sync-a sa serverom, da pozicija
    // ne nestane iz localStorage pre nego što plejer bude spreman)
    const localSaved = progressManager.loadFromLocalStorage();
    const localIsAhead = isLocalProgressAhead(localSaved, initialProgress);
    if (localSaved && localSaved.currentTime > 0) {
      progressManager.recoverFromLocalStorage(initialProgress);
    }

    let resumeTime = 0;
    if (!initialProgress?.isCompleted) {
      resumeTime = localIsAhead
        ? localSaved.currentTime
        : initialProgress?.watchedDuration || 0;
    }

    // --- 2. Kreiraj Vimeo Player ---
    const baseUrl = vimeoHash
      ? `https://player.vimeo.com/video/${vimeoVideoId}?h=${vimeoHash}`
      : `https://player.vimeo.com/video/${vimeoVideoId}`;

    const playerOptions = {
      url: `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}vimeo_logo=0`,
      responsive: true,
      autopause: true,
      dnt: true,
      autoplay: autoPlay,
    };

    let player;
    try {
      player = new Player(containerRef.current, playerOptions);
      playerRef.current = player;
    } catch (error) {
      console.error("Error creating Vimeo player:", error);
      setHasError(true);
      return () => {
        if (progressManagerRef.current) {
          progressManagerRef.current.destroy();
          progressManagerRef.current = null;
        }
      };
    }

    // --- 3. Nastavi od poslednje pozicije ---
    player
      .ready()
      .then(async () => {
        if (resumeTime > 0) {
          try {
            await player.setCurrentTime(resumeTime);
          } catch (e) {
            console.warn("Could not restore playback position:", e);
          }
        }
      })
      .catch((error) => {
        console.error("Vimeo player failed to load:", error);
        setHasError(true);
      });

    // --- 4. Event Listeners ---

    player.on("timeupdate", (data) => {
      progressManager.updateProgress(data.seconds, data.duration);
    });

    player.on("ended", async () => {
      try {
        const result = await progressManager.completeLesson();
        hasCompletedRef.current = true;
        if (onCompletion) {
          // Video je stvarno odgledan do kraja — dozvoljena navigacija
          onCompletion(result, "ended");
        }
      } catch (error) {
        console.error("Error completing lesson:", error);
        const message =
          (typeof error === "string" && error) ||
          error?.message ||
          "Greška prilikom čuvanja završetka lekcije — proverite konekciju.";
        toast.error(message);
      }
    });

    player.on("pause", () => {
      progressManager.saveToServer().catch(() => {});
    });

    // --- 5. Cleanup ---
    return () => {
      if (progressManagerRef.current) {
        progressManagerRef.current.destroy();
        progressManagerRef.current = null;
      }
      if (playerRef.current) {
        playerRef.current.destroy().catch(() => {});
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vimeoVideoId, vimeoHash, lessonId, userId, retryAttempt]);

  const handleRetry = () => {
    setHasError(false);
    setRetryAttempt((attempt) => attempt + 1);
  };

  const handleShieldClick = () => {
    const player = playerRef.current;
    if (!player) return;
    player
      .getPaused()
      .then((paused) => (paused ? player.play() : player.pause()))
      .catch(() => {});
  };

  return (
    <div className="vimeo-player-wrapper">
      {hasError ? (
        <div className="vimeo-player-error">
          <div>
            <h3>Video nije dostupan</h3>
            <p>Došlo je do greške prilikom učitavanja videa.</p>
            <button type="button" onClick={handleRetry}>
              Pokušaj ponovo
            </button>
          </div>
        </div>
      ) : (
        <>
          <div ref={containerRef} className="vimeo-player-container" />
          <div
            className="vimeo-click-shield"
            onContextMenu={(e) => e.preventDefault()}
            onClick={handleShieldClick}
          />
        </>
      )}

      <style>{`
        .vimeo-player-wrapper {
          width: 100%;
          position: relative;
        }

        .vimeo-player-container {
          width: 100%;
          overflow: hidden;
        }

        .vimeo-player-container iframe {
          display: block;
        }

        .vimeo-click-shield {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: calc(100% - 50px);
          z-index: 2;
        }

        .vimeo-player-error {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          aspect-ratio: 16 / 9;
          background: var(--background);
          border: 2px dashed var(--accent-light);
          color: var(--text-secondary);
          text-align: center;
        }

        .vimeo-player-error > div {
          padding: 20px;
          max-width: 300px;
        }

        .vimeo-player-error h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .vimeo-player-error p {
          margin: 0 0 16px 0;
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .vimeo-player-error button {
          padding: 10px 20px;
          background: var(--accent);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .vimeo-player-error button:hover {
          background: var(--accent-dark);
        }
      `}</style>
    </div>
  );
};

export default VimeoPlayer;
